import { type Skeleton, Quaternion } from 'three'
import { type PoseBoneSnapshot, PoseSnapshotUtil } from '../timeline/PoseSnapshot'
import { type SkeletonType } from '../../lib/enums/SkeletonType'
import { get_db, POSES_STORE } from './db'

/**
 * IndexedDB persistence for named poses — snapshots of every bone's local
 * rotation that the user can save and re-apply across clips. Per-skeleton-type
 * scoped (a saved "T-pose" for a human rig won't surface for the fox rig).
 *
 * Apply semantics: when re-applied to a live skeleton, bones are matched by
 * name. Bones present in the pose but missing from the skeleton are skipped.
 * Bones present in the skeleton but missing from the pose keep their current
 * rotation (so partial poses — e.g. just the right arm — are useful too).
 */

export interface SavedPose {
  id: string
  name: string
  skeleton_type: SkeletonType
  created_at: string
  /** Per-bone local rotation (and optional position) snapshot. Reuses the
   *  existing PoseBoneSnapshot shape so PoseSnapshotUtil.capture's output
   *  drops straight in. */
  bones: PoseBoneSnapshot[]
  thumbnail_data_url?: string
}

export class PoseLibraryStorage {
  static async list (skeleton_type?: SkeletonType): Promise<SavedPose[]> {
    try {
      const db = await get_db()
      const all = await db.getAll(POSES_STORE) as unknown[]
      const valid: SavedPose[] = []
      for (const raw of all) {
        if (is_saved_pose(raw)) {
          if (skeleton_type === undefined || raw.skeleton_type === skeleton_type) {
            valid.push(raw)
          }
        }
      }
      valid.sort((a, b) => a.name.localeCompare(b.name))
      return valid
    } catch (err) {
      console.warn('PoseLibraryStorage.list failed:', err)
      return []
    }
  }

  static async put (pose: SavedPose): Promise<boolean> {
    try {
      const db = await get_db()
      await db.put(POSES_STORE, pose)
      return true
    } catch (err) {
      console.warn(`PoseLibraryStorage.put(${pose.id}) failed:`, err)
      return false
    }
  }

  static async delete (id: string): Promise<boolean> {
    try {
      const db = await get_db()
      await db.delete(POSES_STORE, id)
      return true
    } catch (err) {
      console.warn(`PoseLibraryStorage.delete(${id}) failed:`, err)
      return false
    }
  }
}

export function generate_pose_id (): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  let out = ''
  for (let i = 0; i < 32; i++) out += Math.floor(Math.random() * 16).toString(16)
  return out
}

/**
 * Capture the live skeleton's pose for storage. Wraps PoseSnapshotUtil so
 * callers don't need to know about the `time` parameter (irrelevant for a
 * named-library pose).
 */
export function capture_pose_for_library (
  skeleton: Skeleton,
  options?: { position_bone_names?: string[] }
): PoseBoneSnapshot[] {
  return PoseSnapshotUtil.capture(skeleton, 0, options).bones
}

/**
 * Apply a saved pose to a live skeleton in-place. Bones matched by name;
 * unmatched bones (in either direction) are skipped. Returns the count of
 * bones actually updated.
 */
export function apply_pose_to_skeleton (skeleton: Skeleton, bones: PoseBoneSnapshot[]): number {
  const by_name = new Map(skeleton.bones.map(b => [b.name, b] as const))
  let applied = 0
  for (const snap of bones) {
    const bone = by_name.get(snap.bone_name)
    if (bone === undefined) continue
    const q = new Quaternion(snap.quaternion[0], snap.quaternion[1], snap.quaternion[2], snap.quaternion[3])
    bone.quaternion.copy(q)
    if (snap.position !== undefined) {
      bone.position.set(snap.position[0], snap.position[1], snap.position[2])
    }
    bone.updateMatrixWorld(true)
    applied++
  }
  return applied
}

function is_saved_pose (raw: unknown): raw is SavedPose {
  if (typeof raw !== 'object' || raw === null) return false
  const r = raw as Partial<SavedPose>
  if (typeof r.id !== 'string' || typeof r.name !== 'string') return false
  if (typeof r.skeleton_type !== 'string') return false
  if (typeof r.created_at !== 'string') return false
  if (!Array.isArray(r.bones)) return false
  for (const b of r.bones) {
    if (typeof b !== 'object' || b === null) return false
    if (typeof b.bone_name !== 'string') return false
    if (!Array.isArray(b.quaternion) || b.quaternion.length !== 4) return false
  }
  return true
}
